/**
 * The template formatter registry: the single source of truth for what can
 * follow a `|` in a template, e.g. `{{ Expiry Date | date d MMMM yyyy | uppercase }}`.
 *
 * Every consumer reads from here:
 * - `formatValue` (FormatUtils) renders them for `processVar` / `processText`
 *   (communications, tasks, escalations, sidebars).
 * - decisively-ui builds its formatter menus and previews from `FORMATTERS`.
 * - The portal's template autocomplete suggests `formatterSuggestions()`.
 *
 * A chain is split on `|` and applied left to right. A formatter is its name
 * plus optional space-separated arguments (`date d MMMM yyyy`, `decimal 2`).
 * Unknown names are ignored, so an unsupported formatter leaves the value as is.
 *
 * Some formatters came from EdWard's template engine (`lowercase`, `decimal`,
 * `hours`, `exists`, `min`, `max`, `atShiftEnd`, named date formats) so it can
 * move onto this one. Where the two engines disagreed, this one keeps the
 * behaviour Decisively already renders; each such formatter notes the
 * difference in its description.
 */

import { differenceInCalendarDays, format, isMatch, isValid, parse, parseISO } from "date-fns";

/** Value types a formatter is offered for. `any` works on every type. */
export type FormatterValueType = "text" | "number" | "date" | "boolean" | "any";

/** A value's type as a formatter menu sees it: a `FormatterValueType` other than `any`, which no value has. */
export type FormatterMenuType = Exclude<FormatterValueType, "any">;

export interface FormatContext {
  /** The attribute's data type, if known (e.g. "date", "number"). */
  type?: string;
  /** Locale for locale-aware output. Defaults to en-au. */
  locale?: string;
  /** "Now" for relative dates. Defaults to the current time. */
  now?: Date;
  /** Looks up another value by key, for `min` / `max` / `atShiftEnd` arguments. */
  resolve?: (key: string) => unknown;
  /** Named date patterns, so `date shortDate` can mean e.g. `dd/MM/yyyy`. */
  dateFormats?: Record<string, string>;
}

export interface FormatterPreset {
  /** The full formatter string as written in a template, e.g. `date d MMMM yyyy`. */
  formatter: string;
  /** Short menu label, e.g. "Full date". */
  label: string;
}

export interface FormatterDefinition {
  name: string;
  description: string;
  /** Value types a menu should offer it for. */
  appliesTo: FormatterValueType[];
  /** Its optional argument, if it takes one. */
  argument?: { name: string; description: string; example: string };
  /** Ready-made uses of it for menus. Empty for formatters that only make sense hand-written. */
  presets: FormatterPreset[];
  /**
   * Whether it reads the raw value (objects, temporals, null). Other formatters
   * skip an object value and end the chain, leaving the object as is.
   */
  rawValue?: boolean;
  /**
   * Checks its arguments, returning a message for the author when they're wrong.
   * Without one, a formatter with no `argument` rejects any arguments and one with
   * an `argument` accepts anything.
   */
  validate?: (args: string[]) => string | undefined;
  apply: (value: unknown, args: string[], context: FormatContext) => unknown;
}

export const DEFAULT_LOCALE = "en-au";

/** A value as text, the way every text formatter reads it. */
export const toText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : String(value);
};

const toNumber = (value: unknown): number => (typeof value === "number" ? value : Number.parseFloat(toText(value)));

const PARSE_FORMATS = [
  "uuuu-MM-dd'T'HH:mm:ss",
  "uuuu-MM-dd HH:mm:ss",
  "uuuu-MM-dd'T'HH:mm",
  "uuuu-MM-dd HH:mm",
  "uuuu-MM-dd",
  "-yyyyyy-MM-dd",
  "yyyyyy-MM-dd",
  "-yyyyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  "-yyyyyy-MM-dd'T'HH:mm:ss.SSS",
  "+yyyyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  "+yyyyyy-MM-dd'T'HH:mm:ss.SSS",
  "P",
  "P p",
  "P pS",
  "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  "yyyy-MM-dd'T'HH:mm:ss.SSS",
];

/** Reads a date from the formats attribute values are stored in, or undefined. */
export const getDate = (value: string): Date | undefined => {
  if (value?.length === 4) {
    if (!Number.isNaN(Number.parseInt(value))) {
      return new Date(Number.parseInt(value), 0, 1);
    }
  }

  for (const pattern of PARSE_FORMATS) {
    if (isMatch(value, pattern)) {
      return parse(value, pattern, new Date());
    }
  }
  return undefined;
};

/**
 * How a value renders with no formatters: dates become the locale's short date
 * (plus time when there is one), everything else is left alone.
 */
export const formatDefault = (value: string, type: string | undefined, locale = DEFAULT_LOCALE): string => {
  if (type === "number") return value;
  const date = getDate(value);
  if (!date) return value;

  let isDate = type === "date";
  if (!isDate) {
    isDate =
      date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;
  }

  const options: Intl.DateTimeFormatOptions = isDate
    ? {}
    : { hour: "2-digit", minute: "2-digit", day: "numeric", month: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat([locale, "en-AU"], options).format(date).toUpperCase().replace(/,/g, "");
};

/**
 * Reads a date for `relative` and `atShiftEnd`: a Date as is, anything else as
 * text (so a number is never taken as a timestamp), with date-only strings as
 * local midnight.
 */
const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) return isValid(value) ? value : undefined;
  const text = toText(value).trim();
  if (!text) return undefined;
  const date = parseISO(text);
  if (isValid(date)) return date;
  const fallback = new Date(text);
  return isValid(fallback) ? fallback : undefined;
};

/** The most decimal places `toFixed` accepts; it throws a RangeError above this. */
const MAX_PLACES = 100;

/** A decimal place count `toFixed` accepts. */
const isValidPlaces = (places: number): boolean => Number.isInteger(places) && places >= 0 && places <= MAX_PLACES;

/** What's wrong with a decimal place argument (`decimal 2`), if anything. */
const placesError = (name: string, args: string[]): string | undefined => {
  if (args.length > 1 || (args[0] !== undefined && !/^\d+$/.test(args[0]))) {
    return `Takes a whole number of decimal places, e.g. ${name} 2`;
  }
  if (args[0] !== undefined && !isValidPlaces(Number.parseInt(args[0], 10))) {
    return `Takes at most ${MAX_PLACES} decimal places`;
  }
  return undefined;
};

/** Rounds to `places` only when there are more decimals than that; never pads. */
const roundDown = (value: unknown, places: number): string => {
  if (!value && value !== 0) return "-";
  const text = toText(value);
  const [, decimals] = text.split(".");
  return decimals && decimals.length > places && isValidPlaces(places) ? Number(value).toFixed(places) : text;
};

/** Decimal hours as "2hr 30m". Rounds once to whole minutes so it never shows 60m. */
const hoursAndMinutes = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "0";
  const hours = toNumber(value);
  if (!Number.isFinite(hours)) return "0";

  const totalMinutes = Math.round(Math.abs(hours) * 60);
  if (totalMinutes === 0) return "0";

  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [wholeHours > 0 ? `${wholeHours}hr` : "", minutes > 0 ? `${minutes}m` : ""].filter(Boolean);
  return `${hours < 0 ? "-" : ""}${parts.join(" ")}`;
};

/** The comparison value for `min` / `max`: a number, or the key of another value. */
const compareValue = (args: string[], context: FormatContext): unknown => {
  const raw = args.join(" ").replace(/^['"]|['"]$/g, "");
  const number = Number.parseFloat(raw);
  return Number.isNaN(number) ? context.resolve?.(raw) : number;
};

const minOrMax =
  (pick: (a: number, b: number) => number) =>
  (value: unknown, args: string[], context: FormatContext): unknown => {
    if (!args.length) return value;
    const other = compareValue(args, context);
    if (value === undefined) return other;
    const a = toNumber(value);
    const b = toNumber(other);
    return Number.isNaN(a) || Number.isNaN(b) ? value : pick(a, b);
  };

/** The value in force at `date`: ranges are half-open, [start, end), and the last match wins. */
const valueAtDate = (temporal: { ranges?: { r: { start: string; end: string }; v: unknown }[] }, date: unknown) => {
  if (!temporal?.ranges) return undefined;
  // Parsed like the range bounds (as EdWard does), so a date-only day lines up with date-only bounds.
  const at = date instanceof Date ? date : new Date(toText(date));
  if (!isValid(at)) return undefined;
  let current: unknown;
  for (const range of temporal.ranges) {
    if (at >= new Date(range.r.start) && at < new Date(range.r.end)) current = range.v;
  }
  return current;
};

/** Currency by locale region, for `currency <locale>` with no code. */
const REGION_CURRENCIES: Record<string, string> = {
  AU: "AUD",
  NZ: "NZD",
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  IE: "EUR",
  DE: "EUR",
  FR: "EUR",
  ES: "EUR",
  IT: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  FI: "EUR",
  PT: "EUR",
  JP: "JPY",
  SG: "SGD",
  IN: "INR",
};

const CURRENCY_CODE = /^[A-Za-z]{3}$/;

/**
 * `Intl.supportedValuesOf` (ES2022), typed locally so consumers compiling against
 * an older `lib` (the services use ES2021) still type-check this file.
 */
const supportedValuesOf = (Intl as { supportedValuesOf?: (key: "currency") => string[] }).supportedValuesOf;

/** Whether a 3-letter code is a currency the runtime knows (any well-formed code if it can't list them). */
const isKnownCurrency = (code: string): boolean =>
  typeof supportedValuesOf !== "function" || supportedValuesOf("currency").includes(code.toUpperCase());

/**
 * What's wrong with a date-fns pattern, if anything. Y and D (week-year and
 * day-of-year) are almost always meant as y and d, and date-fns warns to the
 * console (and throws for some) for them, so they're rejected up front.
 */
const dateError = (pattern: string): string | undefined => {
  const unquoted = pattern.replace(/'[^']*'/g, "");
  if (/[YD]/.test(unquoted)) {
    return "Use yyyy and dd for year and day (YYYY and DD are week-year and day-of-year)";
  }
  try {
    format(new Date(2026, 0, 1), pattern);
    return undefined;
  } catch {
    return `"${pattern}" isn't a valid date pattern`;
  }
};

/**
 * `currency` with arguments: a 3-letter code and / or a locale, in either order.
 * Undefined when they can't be used (unknown locale, or a locale whose currency
 * isn't known), so the caller can fall back.
 */
const formatMoney = (amount: number, args: string[], defaultLocale: string): string | undefined => {
  const code = args.find((arg) => CURRENCY_CODE.test(arg))?.toUpperCase();
  const locale = args.find((arg) => !CURRENCY_CODE.test(arg));
  try {
    const resolvedLocale = locale ? Intl.getCanonicalLocales(locale)[0] : defaultLocale;
    const currency = code ?? REGION_CURRENCIES[new Intl.Locale(resolvedLocale).maximize().region ?? ""];
    if (!currency) return undefined;
    return new Intl.NumberFormat([resolvedLocale, "en-AU"], { style: "currency", currency }).format(amount);
  } catch {
    // A malformed locale or code.
    return undefined;
  }
};

const define = (definition: FormatterDefinition) => definition;

export const FORMATTERS = {
  uppercase: define({
    name: "uppercase",
    description: "Converts text to upper case.",
    appliesTo: ["text"],
    presets: [{ formatter: "uppercase", label: "UPPERCASE" }],
    apply: (value) => toText(value).toUpperCase(),
  }),
  lowercase: define({
    name: "lowercase",
    description: "Converts text to lower case.",
    appliesTo: ["text"],
    presets: [{ formatter: "lowercase", label: "lowercase" }],
    apply: (value) => toText(value).toLowerCase(),
  }),
  capitalize: define({
    name: "capitalize",
    description: 'Upper-cases the first letter and leaves the rest as is: "jane mitchell" gives "Jane mitchell".',
    appliesTo: ["text"],
    presets: [{ formatter: "capitalize", label: "Capitalize" }],
    apply: (value) => {
      const text = toText(value);
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
  }),
  titleCase: define({
    name: "titleCase",
    description:
      'Upper-cases the first letter of each word (after a space or hyphen) and leaves the rest as is: "jane mitchell-smith" gives "Jane Mitchell-Smith".',
    appliesTo: ["text"],
    presets: [{ formatter: "titleCase", label: "Title Case" }],
    apply: (value) =>
      toText(value).replace(/(^|[\s-])(\p{L})/gu, (_match, before, letter) => before + letter.toUpperCase()),
  }),
  date: define({
    name: "date",
    description:
      "Formats a date. With a date-fns pattern (or a named format from the context) it uses that; with none it uses the locale's short date. An invalid pattern leaves the value as is. EdWard's engine gave an ISO string with no pattern.",
    appliesTo: ["date"],
    argument: {
      name: "pattern",
      description: "A date-fns format pattern, e.g. d MMMM yyyy. Use yyyy and dd, not YYYY and DD.",
      example: "d MMMM yyyy",
    },
    presets: [
      { formatter: "date", label: "Date (locale default)" },
      { formatter: "date d MMMM yyyy", label: "Full date" },
      { formatter: "date d MMM yyyy", label: "Short date" },
      { formatter: "date EEEE d MMMM", label: "Day & date" },
      { formatter: "date dd/MM/yyyy", label: "Numeric date" },
      { formatter: "date yyyy-MM-dd", label: "International" },
      { formatter: "date MMMM yyyy", label: "Month & year" },
    ],
    rawValue: true,
    validate: (args) => (args.length ? dateError(args.join(" ")) : undefined),
    apply: (value, args, context) => {
      if (value === null || value === undefined || (typeof value === "object" && !(value instanceof Date))) {
        return value;
      }
      if (!args.length) return formatDefault(toText(value), context.type, context.locale);
      const pattern = context.dateFormats?.[args.join(" ")] ?? args.join(" ");
      // Anything but a Date is read as text, so a number is never taken as a timestamp.
      const date = value instanceof Date ? value : parseISO(toText(value));
      try {
        return format(date, pattern);
      } catch {
        // An invalid date or pattern leaves the value as is.
        return value;
      }
    },
  }),
  relative: define({
    name: "relative",
    description:
      'Describes a date in days from today: "today", "yesterday", "tomorrow", "3 days ago", "in 3 days". EdWard\'s engine used date-fns formatRelative ("last Sunday at 5:12 PM").',
    appliesTo: ["date"],
    presets: [{ formatter: "relative", label: "Relative" }],
    rawValue: true,
    apply: (value, _args, context) => {
      const date = toDate(value);
      if (!date) return value;
      const days = differenceInCalendarDays(context.now ?? new Date(), date);
      if (days === 0) return "today";
      if (days === 1) return "yesterday";
      if (days === -1) return "tomorrow";
      return days > 0 ? `${days} days ago` : `in ${Math.abs(days)} days`;
    },
  }),
  currency: define({
    name: "currency",
    description:
      'A number as money. With no argument: dollars to 2 places with no separators, e.g. "$1234.50" (and 2.675 gives "$2.67", from float rounding). With a currency code and / or locale it uses the locale\'s symbol, separators and rounding: "currency AUD" gives "$1,234.50" in an en-AU release, "currency EUR de-DE" gives "1.234,50 €". A code alone uses the release locale; a locale alone uses its country\'s currency. Leaves non-numbers as is. EdWard\'s engine always formatted as en-US dollars ("currency USD en-US" here).',
    appliesTo: ["number"],
    argument: {
      name: "code / locale",
      description: "An ISO currency code (AUD, EUR), a locale (en-GB, de-DE), or both, in either order.",
      example: "EUR de-DE",
    },
    presets: [
      { formatter: "currency", label: "Currency, plain" },
      { formatter: "currency AUD", label: "Australian dollars" },
      { formatter: "currency USD en-US", label: "US dollars" },
      { formatter: "currency EUR de-DE", label: "Euros" },
      { formatter: "currency GBP en-GB", label: "Pounds sterling" },
    ],
    validate: (args) => {
      if (args.length > 2) return "Takes a currency code and / or a locale, e.g. currency EUR de-DE";
      const code = args.find((arg) => CURRENCY_CODE.test(arg));
      if (code && !isKnownCurrency(code)) return `"${code}" isn't a currency code`;
      if (args.length === 2 && !code) return "Takes a currency code and / or a locale, e.g. currency EUR de-DE";
      if (args.length && formatMoney(1, args, DEFAULT_LOCALE) === undefined) {
        const locale = args.find((arg) => !CURRENCY_CODE.test(arg)) ?? "";
        return code
          ? `"${locale}" isn't a locale`
          : `"${locale}" isn't a locale with a known currency; add a code, e.g. currency EUR ${locale}`;
      }
      return undefined;
    },
    apply: (value, args, context) => {
      const amount = toNumber(value);
      if (Number.isNaN(amount)) return value;
      if (!args.length) return `$${amount.toFixed(2)}`;
      return formatMoney(amount, args, context.locale ?? DEFAULT_LOCALE) ?? `$${amount.toFixed(2)}`;
    },
  }),
  decimal: define({
    name: "decimal",
    description:
      'Rounds a number with more than the given decimal places (default 3) to that many. Never adds places (1.2 stays "1.2"), but rounding can leave trailing zeros (decimal 2 on 1.005 gives "1.00"). Gives "-" for an empty value.',
    appliesTo: ["number"],
    argument: { name: "places", description: "Maximum decimal places. Defaults to 3.", example: "2" },
    presets: [
      { formatter: "decimal", label: "Rounded, up to 3 places" },
      { formatter: "decimal 2", label: "Rounded, up to 2 places" },
    ],
    validate: (args) => placesError("decimal", args),
    apply: (value, args) => {
      const places = args[0] ? Number.parseInt(args[0], 10) : 3;
      return roundDown(value, Number.isNaN(places) ? 3 : places);
    },
  }),
  decimalHours: define({
    name: "decimalHours",
    description:
      'Hours with " hrs" appended, padded to the given decimal places (default 3): 7.5 gives "7.500 hrs". An invalid place count rounds to at most 3 without padding. Gives "-" for a non-number. EdWard\'s engine took no argument and never padded ("7.5 hrs").',
    appliesTo: ["number"],
    argument: { name: "places", description: "Decimal places. Defaults to 3.", example: "2" },
    presets: [
      { formatter: "decimalHours", label: "Decimal hours" },
      { formatter: "decimalHours 2", label: "Decimal hours, 2 places" },
    ],
    validate: (args) => placesError("decimalHours", args),
    apply: (value, args) => {
      const hours = toNumber(value);
      if (Number.isNaN(hours)) return "-";
      const places = args[0] ? Number.parseInt(args[0], 10) : 3;
      // An unvalidated template can hold any count; one toFixed rejects would otherwise throw.
      if (!isValidPlaces(places)) return `${roundDown(hours, 3)} hrs`;
      return `${hours.toFixed(places)} hrs`;
    },
  }),
  hours: define({
    name: "hours",
    description:
      'Decimal hours as hours and minutes: 2.5 gives "2hr 30m", 0.25 gives "15m". Gives "0" for empty or zero.',
    appliesTo: ["number"],
    presets: [{ formatter: "hours", label: "Hours & minutes" }],
    apply: (value) => hoursAndMinutes(value),
  }),
  min: define({
    name: "min",
    description:
      "The smaller of the value and the argument, which is a number or the key of another value. With no value, the argument.",
    appliesTo: ["number"],
    argument: { name: "value", description: "A number, or the key of another value.", example: "40" },
    presets: [],
    rawValue: true,
    validate: (args) => (args.length ? undefined : "Needs a number or the key of another value, e.g. min 40"),
    apply: minOrMax(Math.min),
  }),
  max: define({
    name: "max",
    description:
      "The larger of the value and the argument, which is a number or the key of another value. With no value, the argument.",
    appliesTo: ["number"],
    argument: { name: "value", description: "A number, or the key of another value.", example: "0" },
    presets: [],
    rawValue: true,
    validate: (args) => (args.length ? undefined : "Needs a number or the key of another value, e.g. max 0"),
    apply: minOrMax(Math.max),
  }),
  exists: define({
    name: "exists",
    description: "true if there is a value (anything but null or undefined), otherwise false.",
    appliesTo: ["any"],
    presets: [],
    rawValue: true,
    apply: (value) => value !== null && value !== undefined,
  }),
  atShiftEnd: define({
    name: "atShiftEnd",
    description:
      'For a temporal value, the value in force at the date held under the given key (default "day"). Ranges run from start up to, not including, end. Gives "" for a value that isn\'t temporal.',
    appliesTo: ["any"],
    argument: { name: "dateKey", description: 'Key of the date to read at. Defaults to "day".', example: "day" },
    presets: [],
    rawValue: true,
    validate: (args) => (args.length > 1 ? "Takes one key, e.g. atShiftEnd day" : undefined),
    apply: (value, args, context) => {
      const temporal = (value as { temporal?: unknown } | null)?.temporal;
      if (!temporal) return "";
      return valueAtDate(temporal as Parameters<typeof valueAtDate>[0], context.resolve?.(args[0] ?? "day"));
    },
  }),
} satisfies Record<string, FormatterDefinition>;

export type FormatterName = keyof typeof FORMATTERS;

const registry: Record<string, FormatterDefinition> = FORMATTERS;

/**
 * The definition registered under a formatter name. Own keys only, so names
 * inherited from Object.prototype (`constructor`, `toString`, `__proto__`) aren't formatters.
 */
const lookup = (name: string): FormatterDefinition | undefined =>
  Object.prototype.hasOwnProperty.call(registry, name) ? registry[name] : undefined;

/** The definition for a formatter string (`date d MMMM yyyy` -> date), if it's a known one. */
export const getFormatter = (formatter: string): FormatterDefinition | undefined =>
  lookup(formatter.trim().split(/\s+/)[0]);

/** Collapses whitespace so `date  d MMMM  yyyy` and `date d MMMM yyyy` are the same formatter. */
export const normaliseFormatter = (formatter: string): string => formatter.trim().replace(/\s+/g, " ");

/** The part after the key in a template (`a | b`) -> formatters, as `processVar` splits it. */
export const parseFormatterChain = (chain: string | undefined): string[] =>
  (chain ?? "").split("|").map(normaliseFormatter).filter(Boolean);

/** Formatters -> the part after the key in a template (`a | b`), the inverse of `parseFormatterChain`. Undefined for none. */
export const formatFormatterChain = (formatters: string[]): string | undefined => {
  const cleaned = formatters.map(normaliseFormatter).filter(Boolean);
  return cleaned.length ? cleaned.join(" | ") : undefined;
};

/** What's wrong with one formatter string (`currency AUD`), if anything. */
export const validateFormatter = (formatter: string): string | undefined => {
  const [name, ...args] = normaliseFormatter(formatter).split(" ");
  const definition = lookup(name);
  if (!definition) return `"${name}" isn't a formatter`;
  if (definition.validate) return definition.validate(args);
  return !definition.argument && args.length ? `${name} doesn't take an argument` : undefined;
};

/**
 * What's wrong with the part of a template after the key (`date MMMM yyyy | uppercase`),
 * if anything: the first bad formatter, an empty step, or braces that would end the template.
 */
export const validateFormatterChain = (chain: string): string | undefined => {
  if (/[{}]/.test(chain)) return "Formatters can't contain { or }";
  if (!chain.trim()) return undefined;
  for (const step of chain.split("|")) {
    if (!step.trim()) return "Missing a formatter between | symbols";
    const error = validateFormatter(step);
    if (error) return error;
  }
  return undefined;
};

/**
 * Applies a chain of formatters to a raw value, left to right. Unknown
 * formatters are skipped. An object value (e.g. a temporal) is only passed to
 * formatters that read raw values; any other formatter ends the chain and the
 * object is returned as is.
 */
export const applyFormatters = (value: unknown, formatters: string[], context: FormatContext = {}): unknown => {
  let result = value;
  for (const formatter of formatters) {
    const [name, ...args] = normaliseFormatter(formatter).split(" ");
    const definition = lookup(name);
    if (!definition) continue;
    if (typeof result === "object" && result !== null && !(result instanceof Date) && !definition.rawValue) {
      return result;
    }
    result = definition.apply(result, args, context);
  }
  return result;
};

/** The formatters a menu should offer for a value type, in registry order. */
export const formattersForType = (type: FormatterValueType): FormatterDefinition[] =>
  Object.values(registry).filter(
    (definition) => definition.appliesTo.includes(type) || definition.appliesTo.includes("any"),
  );

/**
 * The formatter value type for a graph node's value type (`GraphNode.type`), so
 * menus offer the right formatters: `datetime` reads as a date, `currency` as a
 * number, and anything without its own formatters (time, file, set...) as text.
 */
export const formatterValueType = (type: string | undefined): FormatterMenuType => {
  if (type === "date" || type === "datetime") return "date";
  if (type === "number" || type === "currency") return "number";
  if (type === "boolean") return "boolean";
  return "text";
};

/**
 * Every formatter string worth suggesting while typing a template: each preset,
 * plus the bare name of formatters that have none.
 */
export const formatterSuggestions = (): string[] =>
  Object.values(registry).flatMap((definition) =>
    definition.presets.length ? definition.presets.map((preset) => preset.formatter) : [definition.name],
  );
